// Desktop auto-update state for the shell chrome.
//
// The main process (marlin/desktop-application/src/autoUpdates/autoUpdates.ts)
// broadcasts update-status / download-progress / update-error to every
// BrowserWindow and keeps a replayable snapshot behind update:get-status. In V2
// the shell owns the persistent chrome, so the banner lives here rather than in
// an MFE — tide's own copy only ever mounts in standalone (non-MFE) mode, where
// window.electronAPI does not exist, so nothing was listening.
//
// A module-level store rather than component state: the preload bridge's
// on* callbacks are register-only (no unsubscribe), so the listeners are
// attached exactly once for the life of the renderer.

export type UpdateUiStatus =
  | "idle"
  | "available"
  | "downloading"
  | "ready"
  | "restarting";

export interface UpdateSnapshot {
  status: UpdateUiStatus;
  version?: string;
  percent: number;
  dismissed: boolean;
  autoEnabled: boolean;
}

type MainUpdateSnapshot = {
  status: "checking" | "available" | "not-available" | "downloaded" | "error";
  version?: string;
  percent?: number;
  error?: string;
} | null;

type ElectronUpdateApi = {
  getUpdateStatus?: () => Promise<MainUpdateSnapshot>;
  onUpdateStatus?: (
    cb: (event: unknown, status: string, version?: string) => void,
  ) => unknown;
  onDownloadProgress?: (
    cb: (event: unknown, progress: { percent?: number } | undefined) => void,
  ) => unknown;
  onUpdateError?: (cb: (event: unknown, message: string) => void) => unknown;
  startUpdateDownload?: () => Promise<{ success: boolean; error?: string }>;
  installUpdateNow?: () => Promise<{ success: boolean }>;
  getAutoUpdateSettings?: () => Promise<{ enabled: boolean } | undefined>;
  onAutoUpdateSettingsChanged?: (
    cb: (event: unknown, settings: { enabled: boolean } | undefined) => void,
  ) => (() => void) | undefined;
};

function electronApi(): ElectronUpdateApi | undefined {
  return (window as unknown as { electronAPI?: ElectronUpdateApi }).electronAPI;
}

const INITIAL: UpdateSnapshot = {
  status: "idle",
  version: undefined,
  percent: 0,
  dismissed: false,
  autoEnabled: true,
};

let state: UpdateSnapshot = INITIAL;
const listeners = new Set<() => void>();
let started = false;

function set(patch: Partial<UpdateSnapshot>): void {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
}

function setAvailable(version?: string): void {
  set({ status: "available", version, percent: 0, dismissed: false });
}

function setDownloaded(): void {
  set({ status: "ready", percent: 100, dismissed: false });
}

function start(): void {
  if (started) return;
  started = true;

  const api = electronApi();
  if (!api) return;

  // Replay: the check fires ~3s after the main process boots, which can land
  // either side of this renderer mounting.
  api
    .getUpdateStatus?.()
    ?.then((snapshot) => {
      if (!snapshot) return;
      if (snapshot.status === "downloaded") {
        setDownloaded();
      } else if (snapshot.status === "available") {
        setAvailable(snapshot.version);
        if (typeof snapshot.percent === "number" && snapshot.percent > 0) {
          set({ status: "downloading", percent: Math.round(snapshot.percent) });
        }
      }
    })
    ?.catch(() => {
      // The handler is registered by setupAutoUpdater, which runs after the
      // windows are created — an early invoke rejects and the broadcast covers it.
    });

  api.onUpdateStatus?.((_event, status, version) => {
    if (status === "available") setAvailable(version);
    if (status === "downloaded") setDownloaded();
  });

  api.onDownloadProgress?.((_event, progress) => {
    if (typeof progress?.percent === "number") {
      set({ status: "downloading", percent: Math.round(progress.percent) });
    }
  });

  api.onUpdateError?.(() => {
    set({ status: "idle", percent: 0 });
  });

  api.getAutoUpdateSettings?.().then((settings) => {
    if (settings) set({ autoEnabled: settings.enabled });
  });

  api.onAutoUpdateSettingsChanged?.((_event, settings) => {
    if (settings) set({ autoEnabled: settings.enabled });
  });
}

export function subscribeToAutoUpdate(listener: () => void): () => void {
  start();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getAutoUpdateSnapshot(): UpdateSnapshot {
  return state;
}

export function dismissUpdate(): void {
  set({ dismissed: true });
}

export async function startUpdateDownload(): Promise<void> {
  set({ status: "downloading", percent: 0 });
  try {
    await electronApi()?.startUpdateDownload?.();
  } catch {
    // Failures come back over the update-error broadcast.
  }
}

export async function installUpdateNow(): Promise<void> {
  set({ status: "restarting" });
  try {
    await electronApi()?.installUpdateNow?.();
  } catch {
    // Ditto — the app is quitting either way.
  }
}

/** Test seam: the store is module-level and the bridge attaches once. */
export function resetAutoUpdateForTests(): void {
  state = INITIAL;
  started = false;
  listeners.clear();
}
